import React, { useState } from "react";
import { usePermissionContext } from "../contexts/PermissionContext";
import usePermissions from "../hooks/usePermissions";
import { toast } from "react-hot-toast";

const PermissionTest = () => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { refreshUserPermissions, permissionUpdateTrigger } =
    usePermissionContext();
  const {
    userData,
    hasPermission,
    canRead,
    canWrite,
    canDelete,
    isAdmin,
    getUserRole,
    getModulePermissions,
  } = usePermissions();

  const handleRefreshPermissions = async () => {
    setIsRefreshing(true);
    try {
      await refreshUserPermissions();
      toast.success("Permissions refreshed successfully!");
    } catch (error) {
      console.error("Error refreshing permissions:", error);
      toast.error("Failed to refresh permissions");
    } finally {
      setIsRefreshing(false);
    }
  };

  const modules = ["products", "users", "purchase", "supplier", "sales"];

  return (
    <div className="container mx-auto p-6">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">
              Permission Test Page
            </h1>
            <button
              onClick={handleRefreshPermissions}
              disabled={isRefreshing}
              className="bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              {isRefreshing && (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              )}
              {isRefreshing ? "Refreshing..." : "Refresh Permissions"}
            </button>
          </div>

          {/* User Information */}
          <div className="mb-8 p-4 bg-gray-50 rounded-lg">
            <h2 className="text-lg font-semibold mb-4 text-gray-700">
              User Information
            </h2>
            {userData ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="font-medium text-gray-600">Name:</span>
                  <span className="ml-2 text-gray-800">{userData.name}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Role:</span>
                  <span className="ml-2 text-gray-800">
                    {getUserRole() || "N/A"}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Phone:</span>
                  <span className="ml-2 text-gray-800">
                    {userData.phone || "N/A"}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Email:</span>
                  <span className="ml-2 text-gray-800">
                    {userData.email || "N/A"}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Admin:</span>
                  <span className="ml-2 text-gray-800">
                    {isAdmin() ? "Yes" : "No"}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">
                    Update Count:
                  </span>
                  <span className="ml-2 text-gray-800">
                    {permissionUpdateTrigger}
                  </span>
                </div>
              </div>
            ) : (
              <p className="text-gray-500">No user data available</p>
            )}
          </div>

          {/* Permission Matrix */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold mb-4 text-gray-700">
              Permission Matrix
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-4 py-2 text-left">
                      Module
                    </th>
                    <th className="border border-gray-300 px-4 py-2 text-center">
                      Read
                    </th>
                    <th className="border border-gray-300 px-4 py-2 text-center">
                      Write
                    </th>
                    <th className="border border-gray-300 px-4 py-2 text-center">
                      Delete
                    </th>
                    <th className="border border-gray-300 px-4 py-2 text-left">
                      All Permissions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {modules.map((module) => (
                    <tr key={module} className="hover:bg-gray-50">
                      <td className="border border-gray-300 px-4 py-2 font-medium capitalize">
                        {module}
                      </td>
                      <td className="border border-gray-300 px-4 py-2 text-center">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            canRead(module)
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {canRead(module) ? "✓" : "✗"}
                        </span>
                      </td>
                      <td className="border border-gray-300 px-4 py-2 text-center">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            canWrite(module)
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {canWrite(module) ? "✓" : "✗"}
                        </span>
                      </td>
                      <td className="border border-gray-300 px-4 py-2 text-center">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            canDelete(module)
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {canDelete(module) ? "✓" : "✗"}
                        </span>
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        <div className="flex flex-wrap gap-1">
                          {getModulePermissions(module).map((perm) => (
                            <span
                              key={perm}
                              className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs"
                            >
                              {perm}
                            </span>
                          ))}
                          {getModulePermissions(module).length === 0 && (
                            <span className="text-gray-500 text-xs">
                              No permissions
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Raw Permission Data */}
          <div>
            <h2 className="text-lg font-semibold mb-4 text-gray-700">
              Raw Permission Data
            </h2>
            <div className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto">
              <pre className="text-sm">
                {JSON.stringify(userData?.permissions || [], null, 2)}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionTest;
